import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { GlobeFilled, LayerFilled } from '@fluentui/react-icons';
import { USER_GROUPS, SignInContext } from '@modules/users';
import { AccessType } from '@modules/access/types';
import { EFolderType, TItemCardStatus, TRootItems } from '@modules/items/types/folders.types';
import { TAction, TItem } from '@modules/items/types/items.types';
import { isProductFolder, sortProductsByRecentlyOpened } from '@modules/items/helpers/items.helper';
import CountryRegistrationsCard from '@modules/registrations/components/CountryRegistrationsCard';
import { isRegistrationsEnabled } from '@modules/registrations/helpers/registrationAccess';
import { Square } from '@shared/components/Square';
import { SquareButton } from '@shared/components/SquareButton';
import {
  SQUARE_CARD_GRID_GAP,
  SQUARE_CARD_HEIGHT,
  SQUARE_CARD_WIDTH,
  SQUARE_DOUBLE_CARD_HEIGHT,
} from '@shared/components/Square.constants';
import { TERMINOLOGY } from '@shared/i18n';
import RootStatsCard from './RootStatsCard';
import ShowAllProductsCard from './ShowAllProductsCard';
import { HOME_PRODUCT_CARD_COUNT } from './constants';

type TProps = {
  data: TRootItems;
  actions: TAction[];
  onNewProduct: () => void;
  cardStatuses?: Record<string, TItemCardStatus>;
};

const RootItemsGrid = ({ data, actions, onNewProduct, cardStatuses = {} }: TProps) => {
  const { user, organization } = useContext(SignInContext);
  const qmItem = data.children.find((item) => item.folderType === EFolderType.QM);
  const countriesItem = data.children.find((item) => item.folderType === EFolderType.COUNTRY);
  const products = data.children.filter(isProductFolder);
  const homeProducts = sortProductsByRecentlyOpened(products, data.recentProductIds).slice(0, HOME_PRODUCT_CARD_COUNT);
  const isAdmin = user.groups.includes(USER_GROUPS.ADMIN);
  const hasProductBlock = products.length > 0 || isAdmin;
  const numRootCards = [qmItem, countriesItem].filter(Boolean).length;

  const gridRef = useRef<HTMLDivElement>(null);
  const [numColumns, setNumColumns] = useState(1);
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const measure = () => {
      const cardWidth = parseFloat(SQUARE_CARD_WIDTH);
      const gap = parseFloat(SQUARE_CARD_GRID_GAP);
      let columns = numRootCards + (hasProductBlock ? 2 : 0);
      while (columns > 1) {
        const dividerSpace = hasProductBlock && numRootCards > 0 && columns > numRootCards ? gap + 1 : 0;
        if (columns * cardWidth + (columns - 1) * gap + dividerSpace <= grid.clientWidth) break;
        columns -= 1;
      }
      setNumColumns(Math.max(1, columns));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [hasProductBlock, numRootCards]);
  const hasProductDivider = hasProductBlock && numRootCards > 0 && numColumns > numRootCards;

  const countriesCardProps = countriesItem && {
    item: countriesItem,
    href: `/folder/${countriesItem._id}`,
    actions:
      countriesItem.accessRight >= AccessType.EDIT
        ? actions.filter((action) => !['Archive', 'Rename'].includes(action.children))
        : undefined,
    avatarIcon: <GlobeFilled />,
  };

  return (
    <div
      ref={gridRef}
      className="relative grid items-start pb-12"
      style={{
        gridTemplateColumns: Array.from({ length: numColumns }, (_, index) =>
          hasProductDivider && index === numRootCards - 1
            ? `calc(${SQUARE_CARD_WIDTH} + ${SQUARE_CARD_GRID_GAP} + 1px)`
            : SQUARE_CARD_WIDTH
        ).join(' '),
        gridAutoRows: SQUARE_CARD_HEIGHT,
        gap: SQUARE_CARD_GRID_GAP,
        maxWidth: `calc(${numRootCards + 2} * ${SQUARE_CARD_WIDTH} + ${numRootCards + 1} * ${SQUARE_CARD_GRID_GAP}${numRootCards > 0 && hasProductBlock ? ` + ${SQUARE_CARD_GRID_GAP} + 1px` : ''})`,
      }}
    >
      {(qmItem || countriesCardProps) && (
        <div className="contents">
          {qmItem && (
            <div className="row-span-2 w-fit">
              <RootStatsCard
                item={qmItem}
                href={`/folder/${qmItem._id}`}
                stats={[
                  { label: 'Files approved last week', value: data.numRecentApprovedQmItems },
                  { label: 'Users with access', value: data.numQmAccess },
                ]}
                actions={actions.filter((action) => action.children === 'Access')}
                avatarIcon={<LayerFilled />}
                cardStatus={cardStatuses[qmItem._id]}
              />
            </div>
          )}

          {countriesCardProps && (
            <div className="row-span-2 w-fit">
              {isRegistrationsEnabled(organization) ? (
                <CountryRegistrationsCard {...countriesCardProps} />
              ) : (
                <RootStatsCard
                  {...countriesCardProps}
                  stats={[
                    { label: TERMINOLOGY.registrationCountry.upperPlural, value: data.numCountries },
                    { label: TERMINOLOGY.registrationSingle.upperPlural, value: data.numCountryRegistrations },
                  ]}
                />
              )}
            </div>
          )}
        </div>
      )}

      {hasProductBlock && (
        <div className="contents">
          {homeProducts.map((item) => (
            <Square
              key={item._id}
              item={item}
              isRoot
              actions={actions}
              productSubmissionInfos={data.productSubmissionInfos}
              cardStatus={cardStatuses[item._id]}
              avatarCharacterCount={organization.productCardAvatarCharacterCount}
            />
          ))}

          {products.length > HOME_PRODUCT_CARD_COUNT ? (
            <ShowAllProductsCard numProducts={products.length} />
          ) : (
            isAdmin && <SquareButton handleCreate={onNewProduct}>New product</SquareButton>
          )}
        </div>
      )}
      {hasProductDivider && (
        <div
          className="absolute top-0 w-px bg-neutralStroke-2"
          style={{
            left: `calc(${numRootCards} * (${SQUARE_CARD_WIDTH} + ${SQUARE_CARD_GRID_GAP}))`,
            height: SQUARE_DOUBLE_CARD_HEIGHT,
          }}
        />
      )}
    </div>
  );
};

export default RootItemsGrid;
